import { Suspense } from 'react'
import { useLocation } from 'react-router'
import { PORTED_PAGES } from '@/pages'
import { NotFound } from '@/routes/NotFound'
import { WebflowBinder } from '@/components/chrome/WebflowBinder'
import { DocumentHead } from '@/components/chrome/DocumentHead'
import { useRouteModule } from '@/app/moduleCache'
import { portedKey } from '@/app/routePreload'

/**
 * Renders a page ported element-for-element from the capture.
 *
 * Each page is its own chunk, so a visit downloads only its own markup.
 */

/*
 * Each page's module comes from the route cache. The route's loader fetched it
 * before the router switched here, so `useRouteModule` returns it in the same render and
 * the page appears without a blank fallback in between. Identity is stable per
 * route because the module itself is cached, not a component made in render.
 */
function PortedContent({ route }: { route: string }) {
  const loader = PORTED_PAGES[route]
  const Page = useRouteModule(portedKey(route), loader).default
  return <Page />
}

export function PortedPage() {
  const { pathname } = useLocation()
  const route = pathname === '/' ? '/' : pathname.replace(/\/$/, '')
  if (!PORTED_PAGES[route]) return <NotFound />

  /*
   * A full viewport for the fallback, not 60vh.
   *
   * Since Phase 5 the chrome renders before the page chunk resolves, so a
   * short placeholder left the footer sitting at y=580 on a 900px viewport and
   * then shunted it away when the real content arrived. That single shift was
   * the homepage's entire CLS — 0.356 of 0.356. A full-height placeholder
   * keeps the footer below the fold until the page is actually there.
   */
  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <DocumentHead route={route} />
      {/*
        Keyed, because two ported pages share no structure and reconciling one
        into the other leaves stale nodes behind. The chrome around this comes
        from RootLayout and is deliberately not keyed, so the header does not
        remount on navigation.
      */}
      <PortedContent key={route} route={route} />
      {/*
        After the markup so it is in the DOM before the interactions engine
        scans for it. Webflow binds tabs, dropdowns and sliders by querying the
        document; running that against an empty tree leaves every control
        inert.
      */}
      <WebflowBinder key={`bind:${route}`} route={route} />
    </Suspense>
  )
}
